import { useEffect, useRef } from 'react';
import { useLanguageAwareRouting } from './useLanguageAwareRouting';

interface UseSessionCheckProps {
  auth: any; // RustyAuth
  isLoggedIn: boolean;
  logout: () => Promise<boolean>;
  /** Default 10s. */
  checkInterval?: number;
}

/**
 * Polls `accessTokenExpiresAt` and POSTs `/v1/login/refresh` (cookie-credential)
 * when it expires. 401/403 → logout; transient failures retry up to
 * maxConsecutiveFailures with `checkInterval` as the backoff.
 */
export function useSessionCheck({
  auth,
  isLoggedIn,
  logout,
  checkInterval = 10000,
}: UseSessionCheckProps) {
  const { createLanguageURL } = useLanguageAwareRouting();
  const consecutiveFailures = useRef(0);
  const maxConsecutiveFailures = 3;
  const isCheckingRef = useRef(false);

  const toSignIn = () => {
    const next = encodeURIComponent(window.location.hash.slice(1));
    window.location.replace(`#${createLanguageURL('/login')}?next=${next}`);
  };

  const checkSession = async () => {
    if (!isLoggedIn || isCheckingRef.current) {
      return;
    }

    isCheckingRef.current = true;

    try {
      if (!auth?.hasValidAccessToken()) {
        if (consecutiveFailures.current >= maxConsecutiveFailures) {
          console.warn(
            `Too many consecutive refresh failures (${consecutiveFailures.current}), logging out`
          );
          if (!(await logout())) {
            toSignIn();
          }
          return;
        }

        console.info(
          'Access token expired. Refreshing via /v1/login/refresh (cookie credential)...'
        );
        try {
          await auth?.refresh();
          consecutiveFailures.current = 0;
          console.debug('Token refresh successful');
        } catch (error: any) {
          consecutiveFailures.current++;
          console.warn(
            `Token refresh failed (attempt ${consecutiveFailures.current}/${maxConsecutiveFailures}):`,
            error
          );

          // auth.refresh() clears the session on 401/403 and throws; transient
          // failures leave it in place. Either way: give up after the cap.
          if (
            error.message?.includes('Authentication failed') ||
            consecutiveFailures.current >= maxConsecutiveFailures
          ) {
            console.warn('Session invalidated, logging out');
            if (!(await logout())) {
              toSignIn();
            }
          }
        }
      } else {
        consecutiveFailures.current = 0;
      }
    } finally {
      isCheckingRef.current = false;
    }
  };

  useEffect(() => {
    checkSession();

    const intervalId = setInterval(checkSession, checkInterval);

    return () => clearInterval(intervalId);
  }, [isLoggedIn, checkInterval]);

  return { checkSession };
}
